import React, { FC, useState } from 'react';

import {
    CaretDownOutlined,
    CaretRightOutlined,
    StopOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { FileIcon } from '@/components/Common/Icons';
import styles from './BlockedRecipientsSection.module.scss';

const policyStepNameSpace = 'uploadFiles.policyStep';
const nameSpace = 'filesTable.permissionDrawer';

export interface BlockedRecipient {
    email: string;
    blockedFromAll: boolean;
    fileIds: string[];
}

interface BlockedRecipientsSectionProps {
    blockedRecipients: BlockedRecipient[];
    getFileName?: (fileId: string) => string;
    getFileIsFolder?: (fileId: string) => boolean;
    className?: string;
}

const BlockedRecipientsSection: FC<BlockedRecipientsSectionProps> = ({
    blockedRecipients,
    getFileName = (fid) => fid,
    getFileIsFolder = () => false,
    className,
}) => {
    const { t } = useTranslation();

    if (blockedRecipients.length === 0) return null;

    return (
        <div className={`${styles['blocked-section']}${className ? ` ${className}` : ''}`}>
            <div className={styles['blocked-table-header']}>
                <span className={styles['blocked-col-recipient']}>
                    {t(`${policyStepNameSpace}.blockedRecipients`)}
                </span>
                <span className={styles['blocked-col-from']}>
                    {t(`${nameSpace}.blockedFrom`)}
                </span>
            </div>
            <div className={styles['blocked-table-body']}>
                {blockedRecipients.map((recipient) => (
                    <BlockedRecipientRow
                        key={recipient.email}
                        recipient={recipient}
                        getFileName={getFileName}
                        getFileIsFolder={getFileIsFolder}
                    />
                ))}
            </div>
        </div>
    );
};

interface BlockedRecipientRowProps {
    recipient: BlockedRecipient;
    getFileName: (fileId: string) => string;
    getFileIsFolder: (fileId: string) => boolean;
}

const BlockedRecipientRow: FC<BlockedRecipientRowProps> = ({ recipient, getFileName, getFileIsFolder }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const fileCount = recipient.fileIds.length;

    return (
        <div className={styles['blocked-row']}>
            <div className={styles['blocked-row-main']}>
                <div className={styles['blocked-col-recipient']}>
                    {recipient.blockedFromAll ? (
                        <StopOutlined className={styles['blocked-icon-all']} />
                    ) : (
                        <WarningOutlined className={styles['blocked-icon-some']} />
                    )}
                    <span className={styles['blocked-email']}>{recipient.email}</span>
                </div>
                <div className={styles['blocked-col-from']}>
                    {recipient.blockedFromAll ? (
                        <span className={styles['blocked-all-text']}>
                            {t(`${policyStepNameSpace}.blockedFromAll`)}
                        </span>
                    ) : (
                        <button
                            type="button"
                            className={styles['blocked-files-toggle']}
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded
                                ? <CaretDownOutlined className={styles['toggle-icon']} />
                                : <CaretRightOutlined className={styles['toggle-icon']} />}
                            <span>
                                {t(`${policyStepNameSpace}.fileCount`, { count: fileCount, defaultValue: '{{count}} file' })}
                            </span>
                        </button>
                    )}
                </div>
            </div>
            {expanded && !recipient.blockedFromAll && (
                <div className={styles['blocked-files-expanded']}>
                    {recipient.fileIds.map((fid) => (
                        <div key={fid} className={styles['blocked-file-item']}>
                            <FileIcon
                                className={styles['blocked-file-icon']}
                                filename={getFileName(fid)}
                                isFolder={getFileIsFolder(fid)}
                            />
                            <span className={styles['blocked-file-name']}>
                                {getFileName(fid)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default BlockedRecipientsSection;
